import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { 
    Add as AddIcon, 
    Edit as EditIcon, 
    ToggleOn as EnableIcon,
    ToggleOff as DisableIcon 
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/api';
import CertificateUploader from '../../components/CertificateUploader';
import { format } from 'date-fns';

interface Theme {
    theme_id: number;
    title: string;
}

interface QuestionPaper {
    paper_id: number;
    title: string;
    description: string;
    theme_id: number;
    activation_date: string;
    expiry_date: string;
    max_attempts: number;
    grade_brackets: {
        A: { min: number; max: number };
        B: { min: number; max: number };
        C: { min: number; max: number };
        F: { min: number; max: number };
    };
    questions_count: number;
    certificate_path?: string;
    created_at: string;
    updated_at: string;
    is_active: boolean;
    time_per_question: number;
    is_free: boolean;
}

interface QuestionPaperFormData {
    title: string;
    description: string;
    theme_id: number;
    activation_date: string;
    expiry_date: string;
    max_attempts: number;
    grade_brackets: {
        A: { min: number; max: number };
        B: { min: number; max: number };
        C: { min: number; max: number };
        F: { min: number; max: number };
    };
    time_per_question: number;
    is_free: boolean;
}

const defaultGradeBrackets = {
    A: { min: 76, max: 100 },
    B: { min: 51, max: 75 },
    C: { min: 26, max: 50 },
    F: { min: 0, max: 25 },
};

const defaultFormData: QuestionPaperFormData = {
    title: '',
    description: '',
    theme_id: 0,
    activation_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    max_attempts: 3,
    grade_brackets: defaultGradeBrackets,
    time_per_question: 15,
    is_free: false,
};

const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
                {format(date, 'dd MMM yyyy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {format(date, 'hh:mm aa')}
            </Typography>
        </Box>
    );
};

type Order = 'asc' | 'desc';

interface SortConfig {
    field: keyof QuestionPaper | 'theme';
    order: Order;
}

const QuestionPapers: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [papers, setPapers] = useState<QuestionPaper[]>([]);
    const [themes, setThemes] = useState<Theme[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Force reload translations when component mounts
    useEffect(() => {
        i18n.reloadResources().then(() => {
            console.log('Translations reloaded');
        });
    }, [i18n]);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<QuestionPaperFormData>(defaultFormData);
    const [error, setError] = useState<string | null>(null);
    const [editingPaper, setEditingPaper] = useState<QuestionPaper | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        field: 'activation_date',
        order: 'desc'
    });
    
    const { user } = useAuth();
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        if (!user || user.role !== 'content_creator') {
            setError(t('contentCreator.dashboard.accessRequired'));
            setLoading(false);
            return;
        }

        try {
            const [papersResponse, themesResponse] = await Promise.all([
                axios.get('/quiz/papers'),
                axios.get('/quiz/themes')
            ]);
            setPapers(papersResponse.data);
            setThemes(themesResponse.data);
        } catch (error: any) {
            setError(t('contentCreator.questionPapersManagement.loadError'));
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleThemeChange = useCallback((e: any) => {
        setFormData(prev => ({
            ...prev,
            theme_id: e.target.value
        }));
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        try {
            let response;
            if (editingPaper) {
                response = await axios.put(`/quiz/papers/${editingPaper.paper_id}`, formData);
            } else {
                response = await axios.post('/quiz/papers', formData);
            }
            setOpenDialog(false);
            setFormData(defaultFormData);
            setEditingPaper(null);
            fetchData();

            // Navigate to add questions if it's a new paper
            if (!editingPaper && response.data.paper_id) {
                navigate(`/content-creator/papers/${response.data.paper_id}/questions`);
            }
        } catch (error: any) {
            setError(error.response?.data?.error || t('contentCreator.questionPapersManagement.saveError'));
        }
    }, [formData, editingPaper, fetchData, navigate]);

    const handleEdit = useCallback((paper: QuestionPaper) => {
        setEditingPaper(paper);
        setFormData({
            title: paper.title,
            description: paper.description,
            theme_id: paper.theme_id,
            activation_date: paper.activation_date.slice(0, 16), // Format for datetime-local input
            expiry_date: paper.expiry_date.slice(0, 16), // Format for datetime-local input
            max_attempts: paper.max_attempts,
            grade_brackets: paper.grade_brackets,
            time_per_question: paper.time_per_question,
            is_free: paper.is_free || false,
        });
        setOpenDialog(true);
    }, []);

    const handleToggleStatus = useCallback(async (paperId: number, newStatus: boolean) => {
        try {
            await axios.patch(`/quiz/papers/${paperId}`, { is_active: newStatus });
            
            // Update local state
            setPapers(papers.map(paper => 
                paper.paper_id === paperId 
                    ? { ...paper, is_active: newStatus } 
                    : paper
            ));
        } catch (err) {
            console.error('Error toggling paper status:', err);
            setError(t('contentCreator.questionPapersManagement.error.toggleStatus'));
        }
    }, [papers]);

    const handleToggleFree = useCallback(async (paperId: number, newFreeStatus: boolean) => {
        try {
            await axios.patch(`/quiz/papers/${paperId}`, { is_free: newFreeStatus });
            
            // Update local state
            setPapers(papers.map(paper => 
                paper.paper_id === paperId 
                    ? { ...paper, is_free: newFreeStatus } 
                    : paper
            ));
        } catch (err) {
            console.error('Error toggling paper free status:', err);
            setError(t('contentCreator.questionPapersManagement.error.toggleFreeStatus', 'Failed to update free certificate status'));
        }
    }, [papers]);

    const handleSort = (field: SortConfig['field']) => {
        setSortConfig(prev => ({
            field,
            order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
        }));
    };

    const sortedPapers = React.useMemo(() => {
        const sorted = [...papers];
        sorted.sort((a, b) => {
            if (sortConfig.field === 'theme') {
                const themeA = themes.find(t => t.theme_id === a.theme_id)?.title || '';
                const themeB = themes.find(t => t.theme_id === b.theme_id)?.title || '';
                return sortConfig.order === 'asc' 
                    ? themeA.localeCompare(themeB)
                    : themeB.localeCompare(themeA);
            }

            const valueA = a[sortConfig.field];
            const valueB = b[sortConfig.field];

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return sortConfig.order === 'asc'
                    ? valueA.localeCompare(valueB)
                    : valueB.localeCompare(valueA);
            }

            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return sortConfig.order === 'asc'
                    ? valueA - valueB
                    : valueB - valueA;
            }

            return 0;
        });
        return sorted;
    }, [papers, themes, sortConfig]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleManageQuestions = (paperId: number) => {
        navigate(`/content-creator/papers/${paperId}/questions`);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h4">{t('contentCreator.questionPapersManagement.title')}</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setEditingPaper(null);
                                    setFormData(defaultFormData);
                                    setOpenDialog(true);
                                }}
                            >
                                {t('contentCreator.questionPapersManagement.createPaper')}
                            </Button>
                        </Box>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortConfig.field === 'title'}
                                                direction={sortConfig.field === 'title' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('title')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.title')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortConfig.field === 'theme'}
                                                direction={sortConfig.field === 'theme' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('theme')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.theme')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={sortConfig.field === 'activation_date'}
                                                direction={sortConfig.field === 'activation_date' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('activation_date')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.activationDate')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={sortConfig.field === 'expiry_date'}
                                                direction={sortConfig.field === 'expiry_date' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('expiry_date')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.expiryDate')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={sortConfig.field === 'questions_count'}
                                                direction={sortConfig.field === 'questions_count' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('questions_count')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.questions')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">{t('contentCreator.questionPapersManagement.table.certificate')}</TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={sortConfig.field === 'max_attempts'}
                                                direction={sortConfig.field === 'max_attempts' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('max_attempts')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.maxAttempts')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={sortConfig.field === 'time_per_question'}
                                                direction={sortConfig.field === 'time_per_question' ? sortConfig.order : 'asc'}
                                                onClick={() => handleSort('time_per_question')}
                                            >
                                                {t('contentCreator.questionPapersManagement.table.timePerQuestion')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">{t('contentCreator.questionPapersManagement.table.status', 'Status')}</TableCell>
                                        <TableCell align="center">{t('contentCreator.questionPapersManagement.table.free', 'Free')}</TableCell>
                                        <TableCell align="right">{t('contentCreator.questionPapersManagement.table.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedPapers.map((paper) => {
                                        const isExpired = paper.expiry_date 
                                            ? new Date(paper.expiry_date) < new Date() 
                                            : false;
                                        return (
                                            <TableRow 
                                                key={paper.paper_id}
                                                sx={{ 
                                                    backgroundColor: isExpired 
                                                        ? 'action.disabledBackground' 
                                                        : !paper.is_active 
                                                            ? 'rgba(0, 0, 0, 0.04)' // light gray for disabled papers
                                                            : 'inherit'
                                                }}
                                            >
                                                <TableCell>{paper.title}</TableCell>
                                                <TableCell>{themes.find(t => t.theme_id === paper.theme_id)?.title}</TableCell>
                                                <TableCell align="center">{formatDate(paper.activation_date)}</TableCell>
                                                <TableCell align="center">{formatDate(paper.expiry_date)}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => navigate(`/content-creator/papers/${paper.paper_id}/questions`)}
                                                        sx={{ 
                                                            py: 0.5,
                                                            minHeight: '24px',
                                                            lineHeight: 1,
                                                            width: '110px'
                                                        }}
                                                    >
                                                        {t('contentCreator.questionPapersManagement.table.questionsButton', { count: paper.questions_count })}
                                                    </Button>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <CertificateUploader
                                                        paperId={paper.paper_id}
                                                        onUploadSuccess={fetchData}
                                                        certificatePath={paper.certificate_path || undefined}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">{paper.max_attempts || 3}</TableCell>
                                                <TableCell align="center">{paper.time_per_question || 15}s</TableCell>
                                                <TableCell align="center">
                                                    <IconButton 
                                                        onClick={() => handleToggleStatus(paper.paper_id, !paper.is_active)}
                                                        color={paper.is_active ? "primary" : "default"}
                                                        title={paper.is_active ? t('contentCreator.questionPapersManagement.table.active') : t('contentCreator.questionPapersManagement.table.inactive')}
                                                    >
                                                        {paper.is_active ? <EnableIcon /> : <DisableIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        onClick={() => handleToggleFree(paper.paper_id, !paper.is_free)}
                                                        color={paper.is_free ? "primary" : "default"}
                                                        title={paper.is_free ? t('contentCreator.questionPapersManagement.table.freeEnabled') : t('contentCreator.questionPapersManagement.table.freeDisabled')}
                                                    >
                                                        {paper.is_free ? <EnableIcon /> : <DisableIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <IconButton 
                                                            onClick={() => handleEdit(paper)} 
                                                            size="small"
                                                            title={t('contentCreator.questionPapersManagement.table.tooltips.edit')}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Box>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingPaper ? t('contentCreator.questionPapersManagement.editPaper') : t('contentCreator.questionPapersManagement.createPaper')}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>{t('contentCreator.questionPapersManagement.form.theme')}</InputLabel>
                                    <Select
                                        value={formData.theme_id}
                                        onChange={handleThemeChange}
                                        label={t('contentCreator.questionPapersManagement.form.theme')}
                                    >
                                        {themes.map((theme) => (
                                            <MenuItem key={theme.theme_id} value={theme.theme_id}>
                                                {theme.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    name="title"
                                    label={t('contentCreator.questionPapersManagement.form.title')}
                                    value={formData.title}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    name="description"
                                    label={t('contentCreator.questionPapersManagement.form.description')}
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="datetime-local"
                                    name="activation_date"
                                    label={t('contentCreator.questionPapersManagement.form.activationDate')}
                                    value={formData.activation_date}
                                    onChange={handleInputChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="datetime-local"
                                    name="expiry_date"
                                    label={t('contentCreator.questionPapersManagement.form.expiryDate')}
                                    value={formData.expiry_date}
                                    onChange={handleInputChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contentCreator.questionPapersManagement.form.maxAttempts')}
                                    value={formData.max_attempts}
                                    onChange={(e) => setFormData({ ...formData, max_attempts: parseInt(e.target.value) || 1 })}
                                    inputProps={{ min: 1 }}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contentCreator.questionPapersManagement.form.timePerQuestion')}
                                    value={formData.time_per_question}
                                    onChange={(e) => setFormData({ ...formData, time_per_question: parseInt(e.target.value) || 0 })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body1" sx={{ mr: 2 }}>
                                        {i18n.language === 'ms' ? 'Sijil Percuma' : t('contentCreator.questionPapersManagement.form.isFree', 'Free Certificate')}
                                    </Typography>
                                    <IconButton 
                                        color={formData.is_free ? "primary" : "default"}
                                        onClick={() => setFormData({ ...formData, is_free: !formData.is_free })}
                                    >
                                        {formData.is_free ? <EnableIcon /> : <DisableIcon />}
                                    </IconButton>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        {formData.is_free 
                                            ? (i18n.language === 'ms' ? 'Sijil adalah percuma tanpa mengira status langganan' : t('contentCreator.questionPapersManagement.form.freeEnabled', 'Certificate is free regardless of subscription status'))
                                            : (i18n.language === 'ms' ? 'Sijil mengikut peraturan langganan biasa' : t('contentCreator.questionPapersManagement.form.freeDisabled', 'Certificate follows normal subscription rules'))}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t('contentCreator.questionPapersManagement.form.cancel')}</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {editingPaper ? t('contentCreator.questionPapersManagement.form.saveChanges') : t('contentCreator.questionPapersManagement.form.create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default QuestionPapers;
